import { useEffect } from "react";

import { AppUI, selectUIMode } from "@/features/app/appSlice";
import {
  selectCurrentLocation,
  selectCurrentLocationStatus,
  selectHasUsableLocation,
} from "@/features/location/locationSlice";

import { useAppSelector, useBreakpoints } from "@/app/hooks";
import { useRefListener } from "@/app/hooks/useRefListener";
import {
  computeInitialWASMNavigation,
  updateNativeNavigationRoute,
} from "@/features/native_routes/actions";
import {
  NativeRoutesLibState,
  useNativeRoutes,
} from "@/features/native_routes/NativeRoutesProvider";
import { useSitumAPIContext } from "@/features/situm_api/SitumAPIProvider";
import { LocationStatusTypes } from "@/features/utils/types";
import { NavigationStatus } from "@/libs/models";
import {
  completeNavigationStartRequest,
  failPendingNavigationRequest,
  hasBlockingLocationError,
} from "../actions";
import {
  selectNavigationFrom,
  selectNavigationRouteType,
  selectNavigationStatus,
  selectNavigationTo,
  selectPendingNavigationRequest,
} from "../navigationSlice.selectors";
import { NAVIGATION_ERROR_CODES } from "../types";

const PENDING_NAVIGATION_REQUEST_TIMEOUT_MS = 60_000;

// This hook keeps navigation runtime state in sync and bridges deferred API
// requests into the async start flow when location becomes usable.
const useNavigation = () => {
  const uiMode = useAppSelector(selectUIMode);

  const currentLocation = useAppSelector(selectCurrentLocation);
  const currentLocationStatus = useAppSelector(selectCurrentLocationStatus);
  const hasUsableLocation = useAppSelector(selectHasUsableLocation);
  const navigationTo = useAppSelector(selectNavigationTo);
  const navigationFrom = useAppSelector(selectNavigationFrom);
  const navigationRouteType = useAppSelector(selectNavigationRouteType);
  const pendingNavigationRequest = useAppSelector(
    selectPendingNavigationRequest,
  );
  const isPendingNavigationWaitingForLocation =
    Boolean(pendingNavigationRequest) && !hasUsableLocation;

  const {
    initNativeLibrary,
    routesCalculator,
    state: nativeRoutesLibraryState,
    setState: setNativeRoutesLibraryState,
  } = useNativeRoutes();
  const situmAPIContext = useSitumAPIContext();
  const { isTabletVersion } = useBreakpoints();
  const navigationStatus = useAppSelector(selectNavigationStatus);

  // Keeps a timeout for deferred requests so they fail if location never
  // becomes available.
  useEffect(() => {
    if (!isPendingNavigationWaitingForLocation || !pendingNavigationRequest) {
      return;
    }

    const timeoutId = setTimeout(() => {
      failPendingNavigationRequest(
        NAVIGATION_ERROR_CODES.no_location,
        pendingNavigationRequest.payload,
      );
    }, PENDING_NAVIGATION_REQUEST_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [pendingNavigationRequest, isPendingNavigationWaitingForLocation]);

  // Fails immediately for terminal location states or invalid OOB cases.
  useEffect(() => {
    if (!pendingNavigationRequest) {
      return;
    }

    if (hasBlockingLocationError()) {
      failPendingNavigationRequest(
        NAVIGATION_ERROR_CODES.no_location,
        pendingNavigationRequest.payload,
      );
      return;
    }

    if (!currentLocation && currentLocationStatus === LocationStatusTypes.OOB) {
      failPendingNavigationRequest(
        NAVIGATION_ERROR_CODES.no_location,
        pendingNavigationRequest.payload,
      );
    }
  }, [currentLocation, currentLocationStatus, pendingNavigationRequest]);

  // Starts the real navigation flow once the request can be resolved with a
  // usable location. Cleanup aborts the previous start when dependencies
  // change, so only the latest request can keep advancing.
  useEffect(() => {
    if (!pendingNavigationRequest || !hasUsableLocation) {
      return;
    }

    const abortController = new AbortController();

    void completeNavigationStartRequest(
      pendingNavigationRequest,
      situmAPIContext,
      {
        initNativeLibrary,
        routesCalculator,
        setState: setNativeRoutesLibraryState,
        state: nativeRoutesLibraryState,
      },
      abortController.signal,
    ).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      console.error(error);
    });

    return () => abortController.abort();
  }, [
    hasUsableLocation,
    initNativeLibrary,
    nativeRoutesLibraryState,
    pendingNavigationRequest,
    routesCalculator,
    setNativeRoutesLibraryState,
    situmAPIContext,
  ]);

  // Reset current navigation if navigation library reinitializes
  useEffect(() => {
    if (
      // Only run when we are setting up the library
      nativeRoutesLibraryState !== NativeRoutesLibState.SETTING_UP
    ) {
      return;
    }

    // If we are not navigating we inmediately set the library as ready
    if (
      navigationStatus == NavigationStatus.FINISHED ||
      navigationStatus == NavigationStatus.STOPPED
    ) {
      setNativeRoutesLibraryState(NativeRoutesLibState.READY);
    } else {
      // Else we mock a navigation computation
      computeInitialWASMNavigation({
        navigationFrom,
        navigationTo,
        notifyNavigationStart: false,
        routesCalculator,
        type: navigationRouteType,
      });
      setNativeRoutesLibraryState(NativeRoutesLibState.READY);
    }
  }, [nativeRoutesLibraryState, navigationStatus]);

  const updateNavigationRouteListener = useRefListener(() => {
    if (
      // Block navigation updates until library is ready to be used
      nativeRoutesLibraryState !== NativeRoutesLibState.READY ||
      // TODO: it does not make much sense to not re-compute on outdoor locations,
      // we should have an error page in case this happens like on mobile
      (!isTabletVersion && currentLocation?.isOutdoor)
    ) {
      return;
    }
    updateNativeNavigationRoute(routesCalculator);
  }, [
    nativeRoutesLibraryState,
    routesCalculator,
    currentLocation?.isOutdoor,
    currentLocation?.x,
    currentLocation?.y,
    currentLocation?.bearing,
    currentLocation?.accuracy,
  ]);

  // Update native navigation every 2 seconds
  useEffect(() => {
    if (
      uiMode != AppUI.navigation ||
      navigationStatus == NavigationStatus.STOPPED ||
      navigationStatus == NavigationStatus.FINISHED ||
      navigationStatus == NavigationStatus.STARTING
    ) {
      return;
    }

    const interval = setInterval(() => {
      updateNavigationRouteListener.current();
    }, 2000);

    return () => clearInterval(interval);
  }, [uiMode, navigationStatus]);
};

export default useNavigation;
